import { ref, readonly } from 'vue';
import axios from '@/plugins/axios';

// cache for system settings
let systemSettingsCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = ref(5 * 60 * 1000); // 5 minutes

// cache for notifications
let notificationsCache = null;
let notificationsLastFetchTime = 0;
const MAX_CACHED_NOTIFICATIONS = 50;

export const isDuplicateModelRunQueue = (list, incoming) => {
  if (incoming?.type !== 'model_run.queue') return false;
  const incomingRunId = String(incoming.metadata?.eval_run_id ?? '');
  const incomingPosition = Number(incoming.metadata?.queue_position);
  return (list || []).some(
    (item) =>
      item.type === 'model_run.queue' &&
      String(item.metadata?.eval_run_id ?? '') === incomingRunId &&
      Number(item.metadata?.queue_position) === incomingPosition,
  );
};

// cache for file processing
let isFileProcessing = ref(false);

export function useStorage() {

  const getTheme = () => {
    return localStorage.getItem('theme') || 'dark';
  }

  const getUserInfo = () => {
    const data = localStorage.getItem('userInfos');
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error('Error parsing user info:', e);
      return null;
    }
  };

  const getSystemSetting = (name) => {
    const settings = localStorage.getItem('systemSettings');
    if (!settings) return null;
    try {
      const settingsObj = JSON.parse(settings).filter(setting => setting.name === name);
      return settingsObj[0]?.value ?? null;
    } catch (e) {
      console.error('Error parsing system settings:', e);
      return null;
    }
  };

  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };
  
  const getNotificationsCacheKey = () => {
    const userInfo = getUserInfo();
    return `notifications_${userInfo?.id}`;
  };

  const getNotifications = () => {
    try {
      const cacheKey = getNotificationsCacheKey();
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        // Verificar se o cache não expirou
        if (data.timestamp && (Date.now() - data.timestamp) < CACHE_DURATION.value) {
          notificationsCache = data;
          notificationsLastFetchTime = data.timestamp;
          return {
            notifications: data.notifications || [],
            unreadCount: data.unreadCount || 0,
            timestamp: data.timestamp
          };
        } else {
          // Cache expirado, limpar
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.error('Error parsing notifications:', error);
      const cacheKey = getNotificationsCacheKey();
      localStorage.removeItem(cacheKey);
    }
    return null;
  };

  const saveNotifications = (notifications, unreadCount) => {
    try {
      const cacheKey = getNotificationsCacheKey();
      const dataToCache = {
        notifications: notifications.slice(0, MAX_CACHED_NOTIFICATIONS),
        unreadCount: unreadCount || 0,
        timestamp: Date.now()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      notificationsCache = dataToCache;
      notificationsLastFetchTime = Date.now();
      
      return true;
    } catch (error) {
      console.error('Error saving notifications:', error);
      
      // Se localStorage estiver cheio, limpar cache de notificações
      if (error.name === 'QuotaExceededError') {
        clearNotificationsCache();
        return false;
      }
      return false;
    }
  };

  const isNotificationsCacheValid = () => {
    return notificationsCache && 
           (Date.now() - notificationsLastFetchTime) < CACHE_DURATION.value;
  };

  const clearNotificationsCache = () => {
    const cacheKey = getNotificationsCacheKey();
    localStorage.removeItem(cacheKey);
    notificationsCache = null;
    notificationsLastFetchTime = 0;
  };

  const updateNotificationInCache = (notificationId, updates) => {
    try {
      const cached = getNotifications();
      if (cached?.notifications) {
        const notifications = cached.notifications;
        const index = notifications.findIndex(n => n.id === notificationId);
        
        if (index > -1) {
          // update notification
          notifications[index] = { ...notifications[index], ...updates };
          
          // recalculate unread count if necessary
          let unreadCount = cached.unreadCount;
          if (updates.readed && !notifications[index].readed) {
            unreadCount = Math.max(0, unreadCount - 1);
          }
          
          // save again
          return saveNotifications(notifications, unreadCount);
        }
      }
    } catch (error) {
      console.error('Error updating notification in cache:', error);
    }
    return false;
  };

  const removeNotificationFromCache = (notificationId) => {
    try {
      const cached = getNotifications();
      if (cached?.notifications) {
        const notifications = cached.notifications;
        const index = notifications.findIndex(n => n.id === notificationId);
        
        if (index > -1) {
          const notification = notifications[index];
          let unreadCount = cached.unreadCount;
          
          // adjust unread count if notification was not read
          if (!notification.readed) {
            unreadCount = Math.max(0, unreadCount - 1);
          }
          
          // remove notification
          notifications.splice(index, 1);
          
          // save again
          return saveNotifications(notifications, unreadCount);
        }
      }
    } catch (error) {
      console.error('Error removing notification from cache:', error);
    }
    return false;
  };

  const addNotificationToCache = (notification) => {
    try {
      const cached = getNotifications() || { notifications: [], unreadCount: 0 };
      
      // check if notification already exists (avoid duplicates)
      const exists =
        cached.notifications.some((n) => n.id === notification.id) ||
        isDuplicateModelRunQueue(cached.notifications, notification);
      if (!exists) {
        // add to beginning
        cached.notifications.unshift(notification);
        
        // increment unread count if notification is not read
        let unreadCount = cached.unreadCount;
        if (!notification.readed) {
          unreadCount += 1;
        }
        
        // save
        return saveNotifications(cached.notifications, unreadCount);
      }
    } catch (error) {
      console.error('Error adding notification to cache:', error);
    }
    return false;
  };

  const chatQuestionsAndAnswersCacheKey = ref(null);
  const chatQuestionsAndAnswersCache = ref(null);
  const chatQuestionsAndAnswersLastFetchTime = ref(0);

  const getChatQuestionsAndAnswersCacheKey = () => {
    const userInfo = getUserInfo();
    return `chat_questions_and_answers_${userInfo?.id}`;
  };

  const getChatQuestionsAndAnswersFromCache = () => {
    const cacheKey = getChatQuestionsAndAnswersCacheKey();
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }
  };

  const saveChatQuestionsAndAnswersToCache = (chatQuestionsAndAnswers) => {
    try {
      const cacheKey = getChatQuestionsAndAnswersCacheKey();
      localStorage.setItem(cacheKey, JSON.stringify({...chatQuestionsAndAnswers, timestamp: Date.now()}));
      chatQuestionsAndAnswersCache.value = chatQuestionsAndAnswers;
      chatQuestionsAndAnswersLastFetchTime.value = Date.now();
      return true;
    } catch (error) {
      console.error('Error saving chat questions and answers:', error);
      return false;
    }
  };

  const clearChatQuestionsAndAnswersCache = () => {
    const cacheKey = getChatQuestionsAndAnswersCacheKey();
    localStorage.removeItem(cacheKey);
    chatQuestionsAndAnswersCache.value = null;
    chatQuestionsAndAnswersLastFetchTime.value = 0;
    chatQuestionsAndAnswersCacheKey.value = null;
  };

  const updateChatQuestionsAndAnswersInCache = (questionId, updates) => {
    try {
      const cached = getChatQuestionsAndAnswersFromCache() || chatQuestionsAndAnswersCache.value;
      if (cached?.chatQuestionsAndAnswers) {
        const chatQuestionsAndAnswers = cached.chatQuestionsAndAnswers;
        const index = chatQuestionsAndAnswers.findIndex(q => q.question === questionId);
  
          // update question and answer
          chatQuestionsAndAnswers[index] = { ...chatQuestionsAndAnswers[index], ...updates };
          
          // save again
          return saveChatQuestionsAndAnswersToCache(chatQuestionsAndAnswers);
      }
    } catch (error) {
      console.error('Error updating chat question and answer in cache:', error);
    }
    return false;
  };

  const removeChatQuestionsAndAnswersFromCache = (questionId) => {
    try {
      const cached = getChatQuestionsAndAnswersFromCache() || chatQuestionsAndAnswersCache.value;
      if (cached?.chatQuestionsAndAnswers) {
        const chatQuestionsAndAnswers = cached.chatQuestionsAndAnswers;
        const index = chatQuestionsAndAnswers.findIndex(q => q.question === questionId);

        if (index > -1) {
          // remove question and answer
          chatQuestionsAndAnswers.splice(index, 1);
          
          // save again
          return saveChatQuestionsAndAnswersToCache(chatQuestionsAndAnswers);
        }
      }
    } catch (error) {
      console.error('Error removing chat question and answer from cache:', error);
    }
    return false;
  };

  const userInfo = ref(getUserInfo());
  const userAuthToken = ref(getAuthToken());
  const systemSettings = ref(getSystemSetting());

  // Save user info to storage
  const saveUserInfo = async (info) => {
    localStorage.setItem('userInfos', JSON.stringify(info));
    userInfo.value = info;
  };

  const saveAuthToken = async (token) => {
    localStorage.setItem('authToken', token);
    userAuthToken.value = token;
  };

  const verifyAuthToken = () => !!userAuthToken.value;

  const verifyUserInfo = () => !!userInfo.value

  const clearUserData = () => {
    localStorage.clear();
    userInfo.value = null;
    userAuthToken.value = null;
    // clear system settings cache
    systemSettingsCache = null;
    lastFetchTime = 0;
    // clear notifications cache
    notificationsCache = null;
    notificationsLastFetchTime = 0;
  };

  const isLoggedIn = () => {
    return !!userInfo.value;
  };

  const hasPermission = (permission) => {
    const userInfo = getUserInfo();
    return userInfo?.permissions?.includes(permission);
  };

  const hasFlag = (flag) => {
    const userInfo = getUserInfo();
    return userInfo?.flags?.includes(flag);
  };

  const saveSystemSettings = async (settings) => {
    localStorage.setItem('systemSettings', JSON.stringify(settings));
    systemSettings.value = settings;
    // update system settings cache
    systemSettingsCache = settings;
    lastFetchTime = Date.now();
  };

  // function to load system settings with cache
  const loadSystemSettings = async (forceRefresh = false) => {
    const now = Date.now();
    
    // if there is a valid cache and not forced refresh, use the cache
    if (!forceRefresh && systemSettingsCache && (now - lastFetchTime) < CACHE_DURATION.value) {
      return systemSettingsCache;
    }

    try {
      const response = await axios.get('/api/system-settings/public-settings');
      await saveSystemSettings(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading system settings:', error);
      // if failed and there is cache, use the cache even expired
      if (systemSettingsCache) {
        return systemSettingsCache;
      }
      return null;
    }
  };

  const setSoundEnabled = (enabled) => {
    localStorage.setItem('soundEnabled', String(enabled));
  };

  const getSoundEnabled = () => {
    const stored = localStorage.getItem('soundEnabled');
    return stored === null ? true : stored === 'true';
  };

  const saveListingTempData = (datasetId, listingData) => {
    const cacheKey = `listing_temp_data_${datasetId}`;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        ...listingData,
        savedAt: Date.now(),
        datasetId
      }));
      return true;
    } catch (error) {
      console.error('Error saving listing temp data:', error);
      return false;
    }
  };

  const getListingTempDataByDataset = (datasetId) => {
    const cacheKey = `listing_temp_data_${datasetId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }
    return null;
  };

  const removeListingTempData = (datasetId) => {
    const cacheKey = `listing_temp_data_${datasetId}`;
    try {
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      console.error('Error removing listing temp data:', error);
      return false;
    }
  };

  const clearAllTempData = () => {
    const tempData = localStorage.getItem('listing_temp_data');
    if (tempData) {
      localStorage.removeItem('listing_temp_data');
    }
  };

  const setIsFileProcessingValue = (value) => {
    isFileProcessing.value = value;
  };

  const getIsFileProcessingValue = () => {
    return isFileProcessing.value;
  };

  return {
    userInfo: readonly(userInfo),
    userAuthToken: readonly(userAuthToken),
    CACHE_DURATION: readonly(CACHE_DURATION),
    saveUserInfo,
    saveAuthToken,
    saveSystemSettings,
    loadSystemSettings,
    getTheme,
    getUserInfo,
    getAuthToken,
    verifyAuthToken,
    verifyUserInfo,
    clearUserData,
    isLoggedIn,
    hasPermission,
    hasFlag,
    getSystemSetting,
    
    // Notifications functions
    getNotifications,
    saveNotifications,
    isNotificationsCacheValid,
    clearNotificationsCache,
    updateNotificationInCache,
    removeNotificationFromCache,
    addNotificationToCache,
    setSoundEnabled,
    getSoundEnabled,

    // Listing temp data functions
    saveListingTempData,
    getListingTempDataByDataset,
    removeListingTempData,
    clearAllTempData,
    setIsFileProcessingValue,
    getIsFileProcessingValue,

    // Chat functions
    getChatQuestionsAndAnswersFromCache,
    saveChatQuestionsAndAnswersToCache,
    clearChatQuestionsAndAnswersCache,
    updateChatQuestionsAndAnswersInCache,
    removeChatQuestionsAndAnswersFromCache,
  };
} 